import { linerForPool } from './reference.js'
import { maxQty } from './merge.js'
import { packagePayload } from './addToCart.js'

const FILTERS = [
	['all', 'All pools'],
	['personal', 'For one birth'],
	['pro', 'Professional / reusable'],
	['roomy', 'Room for a partner'],
	['budget', 'Under $250'],
]

const SORTS = [
	['featured', 'Featured'],
	['price', 'Price'],
	['size', 'Most water'],
]

export function priceShort(n) {
	const value = Number(n) || 0
	return value % 1 === 0 ? `$${value.toFixed(0)}` : `$${value.toFixed(2)}`
}

export function money(n) {
	return `$${(Number(n) || 0).toFixed(2)}`
}

export function defaultSize(pool) {
	if (!pool?.sizes?.length) return null
	if (pool.id === 'tubina-wild') return pool.sizes[0]
	return pool.sizes[1] || pool.sizes[0]
}

export function selectionOf(pool, sel) {
	return {
		size: sel?.size ?? defaultSize(pool),
		color: sel?.color ?? pool.colors?.[0] ?? null,
	}
}

export function variantOf(pool, sel) {
	const selection = selectionOf(pool, sel)
	return (
		pool.variants.find((variant) => (!variant.size || variant.size === selection.size) && (!variant.color || variant.color === selection.color)) ||
		pool.variants[0]
	)
}

export function specOf(pool, sel) {
	const selection = selectionOf(pool, sel)
	if (selection.size && pool.specs?.[selection.size]) return pool.specs[selection.size]
	return pool.specs?.One || pool.specs?.[Object.keys(pool.specs || {})[0]] || { ext: '', int: '—', gal: null }
}

export function initialState(catalog) {
	const sel = {}
	const accSel = {}
	for (const pool of catalog.pools) {
		sel[pool.id] = { size: defaultSize(pool), color: pool.colors?.[0] || null }
	}
	for (const group of catalog.accessories) {
		for (const item of group.items) {
			if (item.options?.[0]) accSel[item.id] = item.options[0].key
		}
	}
	return {
		sel,
		poolId: null,
		liner: null,
		linerQty: 1,
		acc: {},
		accSel,
		filter: 'all',
		sort: 'featured',
		compareOpen: true,
	}
}

export function setFilter(state, filter) {
	return { ...state, filter }
}

export function setSort(state, sort) {
	return { ...state, sort }
}

export function toggleCompare(state) {
	return { ...state, compareOpen: !state.compareOpen }
}

export function setSel(state, poolId, key, value) {
	const current = state.sel[poolId] || {}
	return {
		...state,
		added: false,
		sel: { ...state.sel, [poolId]: { ...current, [key]: value } },
	}
}

export function choosePool(catalog, state, poolId) {
	const pool = catalog.pools.find((item) => item.id === poolId)
	if (!pool) return state
	let sel = state.sel[poolId] || selectionOf(pool)
	let variant = variantOf(pool, sel)
	if (variant?.outOfStock) {
		const available = pool.variants.find((item) => !item.outOfStock)
		if (!available) return state
		sel = { ...sel, size: available.size ?? sel.size, color: available.color ?? sel.color }
		variant = available
	}
	if (variant?.outOfStock) return state
	return {
		...state,
		sel: { ...state.sel, [poolId]: sel },
		poolId,
		liner: pool.linerIncluded ? 'none' : 'custom',
		linerQty: 1,
	}
}

export function setLiner(state, liner) {
	return { ...state, liner, linerQty: Math.max(1, state.linerQty || 1) }
}

function findAccessory(catalog, itemId) {
	for (const group of catalog.accessories) {
		const item = group.items.find((entry) => entry.id === itemId)
		if (item) return item
	}
	return null
}

function accessorySellable(item, state) {
	if (!item?.options) return item
	const key = state.accSel[item.id]
	return item.options.find((option) => option.key === key) || item.options[0]
}

function accessoryQtyKey(item, state) {
	if (!item?.options) return item.id
	const sellable = accessorySellable(item, state)
	return `${item.id}:${sellable.key}`
}

export function bumpLiner(catalog, state, delta) {
	const pool = catalog.pools.find((item) => item.id === state.poolId)
	const liner = currentLiner(catalog, pool, state)
	const cap = liner ? Math.max(1, maxQty(liner) || 1) : 99
	const linerQty = Math.max(1, Math.min(cap, (state.linerQty || 1) + delta))
	return { ...state, linerQty }
}

export function setAccOption(state, itemId, optionKey) {
	return { ...state, accSel: { ...state.accSel, [itemId]: optionKey } }
}

export function bumpAcc(catalog, state, itemId, delta) {
	const item = findAccessory(catalog, itemId)
	if (!item) return state
	const sellable = accessorySellable(item, state)
	if (delta > 0 && sellable?.outOfStock) return state
	const key = accessoryQtyKey(item, state)
	const cap = Math.max(0, Math.min(99, maxQty(sellable)))
	const next = Math.max(0, Math.min(cap, (state.acc[key] || 0) + delta))
	const acc = { ...state.acc }
	if (next <= 0) delete acc[key]
	else acc[key] = next
	return { ...state, acc }
}

export function removeLine(state, line) {
	if (!line?.remove) return state
	if (line.remove.type === 'pool') return { ...state, poolId: null, liner: null }
	if (line.remove.type === 'liner') return { ...state, liner: 'none' }
	if (line.remove.type === 'acc') {
		const acc = { ...state.acc }
		delete acc[line.remove.key]
		return { ...state, acc }
	}
	return state
}

function currentLiner(catalog, pool, state) {
	if (!pool || !state.liner || state.liner === 'none') return null
	if (state.liner === 'universal') {
		if (pool.noUniversal || pool.linerIncluded) return null
		return catalog.liners.universal
	}
	if (state.liner === 'custom') {
		const key = linerForPool(pool, selectionOf(pool, state.sel[pool.id]).size)
		return catalog.liners[key] || null
	}
	return null
}

function poolPasses(pool, state) {
	const spec = specOf(pool, state.sel[pool.id])
	if (state.filter === 'personal') return pool.use === 'personal'
	if (state.filter === 'pro') return pool.use === 'pro'
	if (state.filter === 'roomy') return pool.roomy || (spec.gal || 0) >= 160
	if (state.filter === 'budget') return Math.min(...pool.variants.map((item) => item.price)) < 250
	return true
}

function outsideLabel(ext) {
	const parts = String(ext || '').split(' × ')
	return `${parts.slice(0, 2).join(' × ')} in.`
}

function colorLabel(colors, id) {
	return colors?.[id]?.label || ''
}

export function viewModel(catalog, state, colors) {
	const palette = colors || {}
	let list = catalog.pools.filter((pool) => poolPasses(pool, state))
	if (state.sort === 'price') {
		list = list.slice().sort((a, b) => variantOf(a, state.sel[a.id]).price - variantOf(b, state.sel[b.id]).price)
	} else if (state.sort === 'size') {
		list = list.slice().sort((a, b) => (specOf(b, state.sel[b.id]).gal || 0) - (specOf(a, state.sel[a.id]).gal || 0))
	}

	const pools = list.map((pool) => {
		const sel = state.sel[pool.id]
		const variant = variantOf(pool, sel)
		const spec = specOf(pool, sel)
		const selection = selectionOf(pool, sel)
		const chosen = state.poolId === pool.id
		const linerKey = linerForPool(pool, selection.size)
		const liner = catalog.liners[linerKey]
		const badges = []
		if (chosen) badges.push({ label: 'Selected', tone: 'dark' })
		if (variant.outOfStock) badges.push({ label: 'Out of stock', tone: 'grey' })
		if (pool.linerIncluded) badges.push({ label: 'Liner included', tone: 'green' })
		if (pool.use === 'pro') badges.push({ label: 'Reusable · Pro', tone: 'coral' })
		const colorName = selection.color ? colorLabel(palette, selection.color) : ''
		const showColor = !!(pool.colors && pool.colors.length > 1)
		return {
			id: pool.id,
			name: pool.name,
			brand: pool.brand,
			image: variant.image,
			features: pool.features,
			outOfStock: !!variant.outOfStock,
			priceLabel: priceShort(variant.price),
			variantLabel: [selection.size, showColor ? colorName : null].filter(Boolean).join(' · '),
			ext: outsideLabel(spec.ext),
			gal: spec.gal ? `${spec.gal} gal` : 'See listing',
			mil: pool.mil,
			hasSizes: !!pool.sizes?.length,
			hasColors: showColor,
			colorName,
			sizeOpts: (pool.sizes || []).map((size) => ({ label: size, pressed: selection.size === size })),
			colorOpts: (pool.colors || []).map((color) => ({
				id: color,
				label: colorLabel(palette, color),
				hex: palette[color]?.hex || '#ddd',
				pressed: selection.color === color,
			})),
			linerNote: pool.linerIncluded
				? 'Custom liner in the box'
				: variant.outOfStock
					? 'Currently unavailable'
					: `Pairs with: ${liner?.name || 'liner'} (+${priceShort(liner?.price || 0)})`,
			badges,
			chosen,
			btnLabel: variant.outOfStock ? 'Out of stock' : chosen ? 'Selected for your package' : 'Choose this pool',
		}
	})

	const compareRows = catalog.pools.map((pool, index) => {
		const prices = pool.variants.map((variant) => variant.price)
		const lo = Math.min(...prices)
		const hi = Math.max(...prices)
		const exts = Object.keys(pool.specs).map((key) => pool.specs[key].ext)
		const ints = Object.keys(pool.specs).map((key) => pool.specs[key].int)
		const gals = Object.keys(pool.specs)
			.map((key) => pool.specs[key].gal)
			.filter((gal) => gal != null)
		const allOut = pool.variants.every((variant) => variant.outOfStock)
		const chosen = state.poolId === pool.id
		const liner = catalog.liners[linerForPool(pool, selectionOf(pool, state.sel[pool.id]).size)]
		return {
			id: pool.id,
			name: pool.name,
			brand: pool.brand,
			use: pool.use === 'pro' ? 'Professional' : 'Personal',
			price: lo === hi ? priceShort(lo) : `${priceShort(lo)}–${priceShort(hi)}`,
			ext: exts.join(' / '),
			int: ints.join(' / '),
			gal: gals.length ? `${gals.join(' / ')} gal` : '—',
			mil: pool.mil,
			seat: pool.seat,
			liner: pool.linerIncluded ? 'Included' : allOut ? '—' : `+${priceShort(liner?.price || 0)}`,
			outOfStock: allOut,
			chosen,
			zebra: index % 2 === 1,
			btnLabel: allOut ? 'Sold out' : chosen ? 'Selected' : 'Select',
		}
	})

	const pool = catalog.pools.find((item) => item.id === state.poolId) || null
	const customKey = pool ? linerForPool(pool, selectionOf(pool, state.sel[pool.id]).size) : null
	const custom = customKey ? catalog.liners[customKey] : null
	const linerOpts = []
	if (pool && custom) {
		if (pool.linerIncluded) {
			linerOpts.push({
				key: 'none',
				title: 'Just the included liner',
				desc: 'The custom liner in the box is all you need for one birth.',
				sku: 'Included',
				price: '$0.00',
				disabled: false,
			})
			linerOpts.push({
				key: 'custom',
				title: 'Add a spare custom liner',
				desc: `${custom.name}. Handy for a second birth or a back-up.`,
				sku: `SKU ${custom.sku}`,
				price: money(custom.price),
				disabled: !!custom.outOfStock,
			})
		} else {
			linerOpts.push({
				key: 'custom',
				title: pool.genericLiner ? 'Best-fit liner (recommended)' : 'Custom-fit liner (recommended)',
				desc: `${custom.name}${pool.genericLiner ? ', sized for this pool.' : ', made for this exact pool.'}`,
				sku: `SKU ${custom.sku}`,
				price: money(custom.price),
				disabled: !!custom.outOfStock,
			})
			if (!pool.noUniversal) {
				const universal = catalog.liners.universal
				const warranty = pool.brand === 'Birth Pool in a Box' ? ' Using it may void the Birth Pool in a Box warranty.' : ''
				linerOpts.push({
					key: 'universal',
					title: 'Universal liner',
					desc: `One-size bowl liner that fits most pools.${warranty}`,
					sku: `SKU ${universal.sku}`,
					price: money(universal.price),
					disabled: !!universal.outOfStock,
				})
			}
			linerOpts.push({
				key: 'none',
				title: 'No liner',
				desc: 'I already have a liner for this pool.',
				sku: '—',
				price: '$0.00',
				disabled: false,
			})
		}
	}

	const activeLiner = currentLiner(catalog, pool, state)
	const lines = []
	if (pool) {
		const variant = variantOf(pool, state.sel[pool.id])
		const selection = selectionOf(pool, state.sel[pool.id])
		const showColor = !!(pool.colors && pool.colors.length > 1 && selection.color)
		const detail = [selection.size, showColor ? colorLabel(palette, selection.color) : null].filter(Boolean)
		lines.push({
			kind: 'Pool',
			name: `${pool.brand} ${pool.name}${detail.length ? ` – ${detail.join(', ')}` : ''}`,
			sku: variant.sku,
			qty: 1,
			unit: variant.price,
			bcProductId: variant.bcProductId,
			bcVariantId: variant.bcVariantId,
			product: variant.product,
			variant: variant.variant,
			outOfStock: !!variant.outOfStock,
			remove: { type: 'pool' },
		})
	}
	if (activeLiner) {
		lines.push({
			kind: 'Liner',
			name: activeLiner.name,
			sku: activeLiner.sku,
			qty: state.linerQty,
			unit: activeLiner.price,
			bcProductId: activeLiner.bcProductId,
			bcVariantId: activeLiner.bcVariantId,
			product: activeLiner.product,
			variant: activeLiner.variant,
			outOfStock: !!activeLiner.outOfStock,
			remove: { type: 'liner' },
		})
	}

	const accGroups = catalog.accessories.map((group) => ({
		title: group.group,
		items: group.items.map((item) => {
			const sellable = accessorySellable(item, state)
			const key = accessoryQtyKey(item, state)
			const qty = state.acc[key] || 0
			return {
				id: item.id,
				name: item.name,
				desc: item.desc,
				image: sellable.image || item.image,
				qty,
				outOfStock: !!sellable.outOfStock,
				priceLabel: money(sellable.price),
				hasOpts: !!item.options,
				opts: (item.options || []).map((option) => ({
					key: option.key,
					label: option.label,
					pressed: option.key === (state.accSel[item.id] || item.options[0].key),
				})),
			}
		}),
	}))

	for (const group of catalog.accessories) {
		for (const item of group.items) {
			const choices = item.options ? item.options.map((option) => ({ sellable: option, key: `${item.id}:${option.key}`, label: option.label })) : [{ sellable: item, key: item.id, label: '' }]
			for (const choice of choices) {
				const qty = state.acc[choice.key]
				if (!qty) continue
				lines.push({
					kind: 'Accessory',
					name: item.name + (choice.label ? ` – ${choice.label}` : ''),
					sku: choice.sellable.sku,
					qty,
					unit: choice.sellable.price,
					bcProductId: choice.sellable.bcProductId,
					bcVariantId: choice.sellable.bcVariantId,
					product: choice.sellable.product,
					variant: choice.sellable.variant,
					outOfStock: !!choice.sellable.outOfStock,
					remove: { type: 'acc', key: choice.key },
				})
			}
		}
	}

	let subtotal = 0
	let itemCount = 0
	for (const line of lines) {
		line.total = money(line.unit * line.qty)
		subtotal += line.unit * line.qty
		itemCount += line.qty
	}

	return {
		filters: FILTERS.map(([id, label]) => ({ id, label, pressed: state.filter === id })),
		sorts: SORTS.map(([id, label]) => ({ id, label, pressed: state.sort === id })),
		pools,
		compareRows,
		compareOpen: !!state.compareOpen,
		noPool: !pool,
		hasPool: !!pool,
		linerIncluded: !!pool?.linerIncluded,
		poolShort: pool?.short || '',
		linerOpts: linerOpts.map((option) => ({ ...option, checked: state.liner === option.key })),
		showLinerQty: !!activeLiner,
		linerQty: state.linerQty,
		accGroups,
		lines,
		payload: packagePayload(lines),
		empty: lines.length === 0,
		subtotal: money(subtotal),
		subtotalNumber: subtotal,
		itemCount,
		skuCount: lines.length,
		pkgTitle: pool ? `${pool.short} package` : 'Water birth package',
		warnNoLiner: !!(pool && !pool.linerIncluded && !activeLiner),
	}
}
